import React from 'react';
import {
  Document, Page, Text, View, StyleSheet, Font, Svg, Path, Circle, Rect, G
} from '@react-pdf/renderer';

// ─── Palette ────────────────────────────────────────────────────────────────
const C = {
  bg:         '#0D0D1A',
  surface:    '#13132B',
  card:       '#1A1A35',
  border:     '#2A2A50',
  primary:    '#A855F7',
  secondary:  '#06B6D4',
  accent:     '#F59E0B',
  success:    '#10B981',
  danger:     '#EF4444',
  warning:    '#F97316',
  white:      '#FFFFFF',
  gray100:    '#F3F4F6',
  gray400:    '#9CA3AF',
  gray600:    '#6B7280',
};

const DIM_COLORS = {
  gouvernance: C.primary,
  securite:    C.danger,
  efficacite:  C.secondary,
  strategie:   C.accent,
};

const DIM_LABELS = {
  gouvernance: 'Gouvernance',
  securite:    'Sécurité',
  efficacite:  'Efficacité',
  strategie:   'Stratégie',
};

// ─── Styles ──────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  page: {
    backgroundColor: C.bg,
    padding: 0,
    fontFamily: 'Helvetica',
  },

  // Cover
  coverPage: { backgroundColor: C.bg, padding: 0, position: 'relative' },
  coverGradientBar: { height: 6, backgroundColor: C.primary },
  coverBody: { padding: '48 48 40 48' },
  coverLabel: { fontSize: 10, color: C.primary, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 8 },
  coverTitle: { fontSize: 38, color: C.white, fontFamily: 'Helvetica-Bold', lineHeight: 1.2, marginBottom: 6 },
  coverSubtitle: { fontSize: 16, color: C.gray400, marginBottom: 40 },
  coverBadge: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.card, borderRadius: 12, padding: '14 20', marginBottom: 36, borderWidth: 1, borderColor: C.border, alignSelf: 'flex-start' },
  coverBadgeScore: { fontSize: 52, fontFamily: 'Helvetica-Bold', marginRight: 16 },
  coverBadgeRight: { flexDirection: 'column' },
  coverBadgeLabel: { fontSize: 11, color: C.gray400, marginBottom: 2 },
  coverBadgeLevel: { fontSize: 16, fontFamily: 'Helvetica-Bold', color: C.white },
  coverClientBox: { backgroundColor: C.card, borderRadius: 10, padding: '14 20', borderWidth: 1, borderColor: C.border, marginBottom: 36 },
  coverClientRow: { flexDirection: 'row', marginBottom: 4 },
  coverClientLabel: { fontSize: 10, color: C.gray400, width: 80 },
  coverClientValue: { fontSize: 10, color: C.white, flex: 1 },
  coverFooter: { position: 'absolute', bottom: 28, left: 48, right: 48, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  coverFooterText: { fontSize: 9, color: C.gray600 },

  // Shared page layout
  contentPage: { backgroundColor: C.bg, paddingTop: 0 },
  pageHeader: { backgroundColor: C.surface, padding: '14 40', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32, borderBottomWidth: 1, borderBottomColor: C.border },
  pageHeaderTitle: { fontSize: 10, color: C.gray400 },
  pageHeaderClient: { fontSize: 10, color: C.primary },
  pageBody: { padding: '0 40 32 40' },
  pageFooter: { position: 'absolute', bottom: 20, left: 40, right: 40, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', borderTopWidth: 1, borderTopColor: C.border, paddingTop: 8 },
  pageFooterText: { fontSize: 8, color: C.gray600 },
  pageNum: { fontSize: 8, color: C.gray600 },

  // Section titles
  sectionTitle: { fontSize: 20, fontFamily: 'Helvetica-Bold', color: C.white, marginBottom: 6 },
  sectionSubtitle: { fontSize: 11, color: C.gray400, marginBottom: 24, lineHeight: 1.5 },
  chipLabel: { fontSize: 9, color: C.primary, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 },

  // Cards
  card: { backgroundColor: C.card, borderRadius: 10, padding: '18 20', borderWidth: 1, borderColor: C.border, marginBottom: 16 },
  cardRow: { flexDirection: 'row', gap: 12, marginBottom: 16 },
  cardHalf: { flex: 1, backgroundColor: C.card, borderRadius: 10, padding: '16 18', borderWidth: 1, borderColor: C.border },
  cardTitle: { fontSize: 11, fontFamily: 'Helvetica-Bold', color: C.white, marginBottom: 6 },
  cardText: { fontSize: 10, color: C.gray400, lineHeight: 1.6 },

  // Dimension bars
  dimRow: { marginBottom: 14 },
  dimHeader: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 5 },
  dimLabel: { fontSize: 10, color: C.white, fontFamily: 'Helvetica-Bold' },
  dimScore: { fontSize: 10, color: C.white },
  dimBar: { height: 8, backgroundColor: C.surface, borderRadius: 4, overflow: 'hidden' },
  dimFill: { height: 8, borderRadius: 4 },

  // Risk table
  riskRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 12, gap: 12 },
  riskDot: { width: 10, height: 10, borderRadius: 5, marginTop: 2, flexShrink: 0 },
  riskContent: { flex: 1 },
  riskTitle: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: C.white, marginBottom: 2 },
  riskText: { fontSize: 9, color: C.gray400, lineHeight: 1.5 },

  // Roadmap
  phaseRow: { flexDirection: 'row', marginBottom: 16, gap: 14 },
  phaseNum: { width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  phaseNumText: { fontSize: 11, fontFamily: 'Helvetica-Bold', color: C.white },
  phaseContent: { flex: 1 },
  phaseTitle: { fontSize: 11, fontFamily: 'Helvetica-Bold', color: C.white, marginBottom: 3 },
  phaseText: { fontSize: 9, color: C.gray400, lineHeight: 1.5 },
  phaseDuration: { fontSize: 8, color: C.primary, marginTop: 3 },

  // Tools / offer
  toolRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 10, gap: 10 },
  toolIcon: { width: 28, height: 28, borderRadius: 8, backgroundColor: C.surface, alignItems: 'center', justifyContent: 'center', flexShrink: 0, borderWidth: 1, borderColor: C.border },
  toolContent: { flex: 1 },
  toolName: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: C.white, marginBottom: 2 },
  toolDesc: { fontSize: 9, color: C.gray400, lineHeight: 1.4 },
  lockedBadge: { fontSize: 8, color: C.primary, backgroundColor: '#2D1B4E', borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2, alignSelf: 'flex-start', marginTop: 3 },

  // CTA box
  ctaBox: { backgroundColor: '#1A0D35', borderRadius: 12, padding: '24 28', borderWidth: 2, borderColor: C.primary, marginTop: 4 },
  ctaTitle: { fontSize: 18, fontFamily: 'Helvetica-Bold', color: C.white, marginBottom: 8 },
  ctaText: { fontSize: 10, color: C.gray400, lineHeight: 1.6, marginBottom: 16 },
  ctaHighlight: { fontSize: 11, fontFamily: 'Helvetica-Bold', color: C.primary },

  // Next steps
  stepRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 14, gap: 12 },
  stepCircle: { width: 32, height: 32, borderRadius: 16, backgroundColor: C.primary, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  stepNum: { fontSize: 12, fontFamily: 'Helvetica-Bold', color: C.white },
  stepContent: { flex: 1, paddingTop: 6 },
  stepTitle: { fontSize: 11, fontFamily: 'Helvetica-Bold', color: C.white, marginBottom: 2 },
  stepText: { fontSize: 9, color: C.gray400, lineHeight: 1.5 },

  // Highlight pill
  pill: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.surface, borderRadius: 20, padding: '6 12', borderWidth: 1, borderColor: C.border, marginRight: 8, marginBottom: 8 },
  pillText: { fontSize: 9, color: C.white },
});

// ─── Helper components ────────────────────────────────────────────────────────
const PageHeader = ({ title, client }) => (
  <View style={s.pageHeader}>
    <Text style={s.pageHeaderTitle}>{title}</Text>
    <Text style={s.pageHeaderClient}>{client}</Text>
  </View>
);

const PageFooter = ({ page, total, contact }) => (
  <View style={s.pageFooter} fixed>
    <Text style={s.pageFooterText}>© 2025 — Rapport confidentiel — {contact}</Text>
    <Text style={s.pageNum}>{page} / {total}</Text>
  </View>
);

const DimBar = ({ label, score, color }) => (
  <View style={s.dimRow}>
    <View style={s.dimHeader}>
      <Text style={s.dimLabel}>{label}</Text>
      <Text style={s.dimScore}>{score}/25</Text>
    </View>
    <View style={s.dimBar}>
      <View style={[s.dimFill, { width: `${(score / 25) * 100}%`, backgroundColor: color }]} />
    </View>
  </View>
);

const RiskItem = ({ color, title, text }) => (
  <View style={s.riskRow}>
    <View style={[s.riskDot, { backgroundColor: color }]} />
    <View style={s.riskContent}>
      <Text style={s.riskTitle}>{title}</Text>
      <Text style={s.riskText}>{text}</Text>
    </View>
  </View>
);

const PhaseItem = ({ num, title, text, duration, color }) => (
  <View style={s.phaseRow}>
    <View style={[s.phaseNum, { backgroundColor: color }]}>
      <Text style={s.phaseNumText}>{num}</Text>
    </View>
    <View style={s.phaseContent}>
      <Text style={s.phaseTitle}>{title}</Text>
      <Text style={s.phaseText}>{text}</Text>
      <Text style={s.phaseDuration}>{duration}</Text>
    </View>
  </View>
);

const ToolItem = ({ icon, name, desc, locked }) => (
  <View style={s.toolRow}>
    <View style={s.toolIcon}>
      <Text style={{ fontSize: 14 }}>{icon}</Text>
    </View>
    <View style={s.toolContent}>
      <Text style={s.toolName}>{name}</Text>
      <Text style={s.toolDesc}>{desc}</Text>
      {locked && <Text style={s.lockedBadge}>🔒 Accessible après votre session expert</Text>}
    </View>
  </View>
);

// ─── Risk analysis engine ────────────────────────────────────────────────────
function buildRisks(answers, dims) {
  const risks = [];
  const g = (stepId, qId) => {
    const s = answers[stepId] || {};
    return s[qId];
  };
  const has = (stepId, qId, val) => {
    const v = g(stepId, qId);
    return Array.isArray(v) ? v.includes(val) : v === val;
  };

  if (dims.securite < 12) {
    risks.push({ level: 'critical', title: 'Posture sécurité insuffisante', text: 'Votre score de sécurité révèle des lacunes critiques. Des mots de passe potentiellement vulnérables ou des incidents répétés exposent vos utilisateurs à des risques de compromission.' });
  }
  if (g('risques', 'passwords') === 'md5' || g('risques', 'passwords') === 'clair' || g('risques', 'passwords') === 'nsp') {
    risks.push({ level: 'critical', title: 'Stockage des mots de passe non conforme', text: 'Votre méthode de stockage des mots de passe ne respecte pas les standards actuels (OWASP). Une violation de données exposerait directement les mots de passe de vos utilisateurs.' });
  }
  if (g('risques', 'incidents') === 'regulier') {
    risks.push({ level: 'high', title: 'Incidents d\'authentification récurrents', text: 'Des incidents répétés signalent une architecture fragile. Chaque incident impacte la confiance de vos utilisateurs et peut engager votre responsabilité.' });
  }
  if (g('gestion', 'approche') === 'custom' || g('gestion', 'approche') === 'aucune') {
    risks.push({ level: 'high', title: 'Dépendance technique critique', text: 'Un code d\'authentification maison crée une dette technique majeure et un risque opérationnel si les développeurs clés quittent l\'entreprise.' });
  }
  if (dims.gouvernance < 10) {
    risks.push({ level: 'medium', title: 'Gouvernance IAM absente', text: 'Sans processus formalisés ni référent IAM, votre organisation ne peut pas réagir rapidement aux incidents ou aux nouvelles exigences réglementaires.' });
  }
  if (has('risques', 'conformite', 'rgpd') || has('risques', 'conformite', 'pci')) {
    risks.push({ level: 'medium', title: 'Risque de non-conformité réglementaire', text: 'Vos obligations RGPD/PCI-DSS requièrent des capacités d\'audit, de traçabilité et de gestion du consentement que votre solution actuelle ne garantit probablement pas.' });
  }
  if (dims.efficacite < 12) {
    risks.push({ level: 'low', title: 'Coûts opérationnels élevés', text: `Vos équipes consacrent un temps excessif à l\'authentification. Ce temps pourrait être réinvesti sur votre cœur de métier.` });
  }

  return risks.slice(0, 5);
}

function buildRoadmap(score, answers) {
  const horizon = (answers.ambitions || {}).horizon || '3-6';
  const urgent = score < 40 || horizon === 'lt3';

  return [
    {
      title: 'Audit & Inventaire',
      text: 'Cartographie complète de vos applications, utilisateurs et flux d\'authentification. Identification des risques critiques et des dépendances.',
      duration: urgent ? '2 semaines' : '3 à 4 semaines',
      color: C.primary,
    },
    {
      title: 'Configuration Auth0 & Proof of Concept',
      text: 'Mise en place de votre tenant Auth0, configuration des policies de sécurité, intégration pilote sur 1 application cible.',
      duration: urgent ? '3 semaines' : '4 à 6 semaines',
      color: C.secondary,
    },
    {
      title: 'Migration des utilisateurs',
      text: 'Migration sécurisée des comptes utilisateurs avec stratégie de zéro interruption de service. Tests de charge et validation.',
      duration: urgent ? '2 à 3 semaines' : '4 à 8 semaines',
      color: C.accent,
    },
    {
      title: 'Déploiement & Hypercare',
      text: 'Mise en production progressive, formation de vos équipes, monitoring renforcé et support dédié post-migration.',
      duration: urgent ? '2 semaines' : '2 à 4 semaines',
      color: C.success,
    },
  ];
}

// ─── Main PDF component ──────────────────────────────────────────────────────
export default function IAMReport({ reportData }) {
  const { clientInfo, iamScore, maturity, answers } = reportData;
  const { total, gouvernance, securite, efficacite, strategie } = iamScore;
  const risks = buildRisks(answers, iamScore);
  const roadmap = buildRoadmap(total, answers);
  const contactEmail = 'contact@auth0.expert';
  const clientName = clientInfo.firstName
    ? `${clientInfo.firstName} ${clientInfo.lastName || ''}`.trim()
    : clientInfo.company || 'Votre entreprise';
  const scoreColor = maturity.color.replace('text-', '').replace('-400', '');
  const scoreHex = {
    red: C.danger, orange: C.warning, blue: C.secondary, green: C.success
  }[scoreColor] || C.primary;

  const approche = ((answers.gestion || {}).approche) || 'custom';
  const approcheLabel = {
    custom: 'Code maison',
    libs: 'Bibliothèques open source',
    iam: 'Solution IAM dédiée',
    aucune: 'Pas de gestion structurée',
  }[approche] || approche;

  const usersLabel = {
    lt1k: '< 1 000', '1k10k': '1K – 10K', '10k100k': '10K – 100K', gt100k: '> 100K'
  }[((answers.perimetre || {}).users)] || 'N/A';

  const appsLabel = {
    '1': '1 app', '2-5': '2-5 apps', '6-20': '6-20 apps', gt20: '> 20 apps'
  }[((answers.perimetre || {}).apps)] || 'N/A';

  const objectifs = ((answers.ambitions || {}).objectifs) || [];
  const objectifLabels = {
    secu: 'Sécurité', sso: 'SSO', mfa: 'MFA', couts: 'Réduction coûts',
    conform: 'Conformité', scale: 'Scalabilité', ux: 'Expérience utilisateur'
  };

  const conformite = ((answers.risques || {}).conformite) || [];
  const conformiteStr = conformite.length ? conformite.map(c => ({
    rgpd: 'RGPD', iso: 'ISO 27001', soc2: 'SOC 2', pci: 'PCI-DSS', aucune: 'Aucune'
  }[c] || c)).join(', ') : 'Non précisé';

  return (
    <Document title={`Rapport Maturité IAM — ${clientName}`} author="Auth0.Expert" language="fr">

      {/* ── PAGE 1: COVER ────────────────────────────────────────────────── */}
      <Page size="A4" style={s.page}>
        <View style={{ height: 6, backgroundColor: C.primary }} />
        <View style={{ height: 3, backgroundColor: C.secondary }} />
        <View style={s.coverBody}>
          <Text style={s.coverLabel}>Rapport confidentiel • 2025</Text>
          <Text style={s.coverTitle}>Rapport de Maturité{'\n'}IAM Personnalisé</Text>
          <Text style={s.coverSubtitle}>Diagnostic Auth0 Migration — {clientName}</Text>

          {/* Score badge */}
          <View style={s.coverBadge}>
            <Text style={[s.coverBadgeScore, { color: scoreHex }]}>{total}</Text>
            <View style={s.coverBadgeRight}>
              <Text style={s.coverBadgeLabel}>Score de maturité IAM / 100</Text>
              <Text style={[s.coverBadgeLevel, { color: scoreHex }]}>{maturity.shortLevel}</Text>
              <Text style={{ fontSize: 9, color: C.gray600, marginTop: 2 }}>{maturity.description}</Text>
            </View>
          </View>

          {/* Client info */}
          <View style={s.coverClientBox}>
            {clientInfo.company && (
              <View style={s.coverClientRow}>
                <Text style={s.coverClientLabel}>Entreprise</Text>
                <Text style={s.coverClientValue}>{clientInfo.company}</Text>
              </View>
            )}
            <View style={s.coverClientRow}>
              <Text style={s.coverClientLabel}>Contact</Text>
              <Text style={s.coverClientValue}>{clientName}</Text>
            </View>
            <View style={s.coverClientRow}>
              <Text style={s.coverClientLabel}>Email</Text>
              <Text style={s.coverClientValue}>{clientInfo.email}</Text>
            </View>
            <View style={[s.coverClientRow, { marginBottom: 0 }]}>
              <Text style={s.coverClientLabel}>Date</Text>
              <Text style={s.coverClientValue}>{new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</Text>
            </View>
          </View>

          <Text style={{ fontSize: 10, color: C.gray600, lineHeight: 1.6 }}>
            Ce rapport est strictement confidentiel et établi sur la base de vos réponses au diagnostic de maturité IAM.
            Il constitue une analyse préliminaire et ne remplace pas un audit technique approfondi.
          </Text>
        </View>
        <View style={s.coverFooter}>
          <Text style={s.coverFooterText}>Auth0.Expert — Expert migration Auth0</Text>
          <Text style={s.coverFooterText}>{contactEmail}</Text>
        </View>
      </Page>

      {/* ── PAGE 2: EXECUTIVE SUMMARY ────────────────────────────────────── */}
      <Page size="A4" style={s.contentPage}>
        <PageHeader title="Résumé Exécutif" client={clientName} />
        <View style={s.pageBody}>
          <Text style={s.chipLabel}>Vue d'ensemble</Text>
          <Text style={s.sectionTitle}>Ce que révèle votre diagnostic</Text>
          <Text style={s.sectionSubtitle}>En {wizardConfig_totalQuestions(answers)} questions, nous avons évalué 4 dimensions clés de votre maturité IAM.</Text>

          {/* Score summary card */}
          <View style={[s.card, { borderColor: scoreHex, borderWidth: 1.5, marginBottom: 20 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16, marginBottom: 12 }}>
              <View style={{ width: 60, height: 60, borderRadius: 30, backgroundColor: C.surface, borderWidth: 2, borderColor: scoreHex, alignItems: 'center', justifyContent: 'center' }}>
                <Text style={{ fontSize: 20, fontFamily: 'Helvetica-Bold', color: scoreHex }}>{total}</Text>
              </View>
              <View>
                <Text style={{ fontSize: 14, fontFamily: 'Helvetica-Bold', color: C.white }}>{maturity.level}</Text>
                <Text style={{ fontSize: 9, color: C.gray400 }}>{maturity.description}</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4 }}>
                  <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: scoreHex, marginRight: 5 }} />
                  <Text style={{ fontSize: 9, color: scoreHex, fontFamily: 'Helvetica-Bold' }}>Risque {maturity.urgency}</Text>
                </View>
              </View>
            </View>
            <Text style={{ fontSize: 10, color: C.gray400, lineHeight: 1.6 }}>{maturity.message}</Text>
          </View>

          {/* 4 dimension bars */}
          <View style={s.card}>
            <Text style={[s.cardTitle, { marginBottom: 16 }]}>Détail par dimension</Text>
            <DimBar label="Gouvernance IAM" score={gouvernance} color={DIM_COLORS.gouvernance} />
            <DimBar label="Sécurité" score={securite} color={DIM_COLORS.securite} />
            <DimBar label="Efficacité opérationnelle" score={efficacite} color={DIM_COLORS.efficacite} />
            <DimBar label="Maturité stratégique" score={strategie} color={DIM_COLORS.strategie} />
          </View>

          {/* Key facts */}
          <View style={s.cardRow}>
            <View style={s.cardHalf}>
              <Text style={s.cardTitle}>Votre périmètre</Text>
              <Text style={s.cardText}>{usersLabel} utilisateurs{'\n'}{appsLabel}{'\n'}Solution actuelle : {approcheLabel}</Text>
            </View>
            <View style={s.cardHalf}>
              <Text style={s.cardTitle}>Conformité</Text>
              <Text style={s.cardText}>{conformiteStr}{'\n\n'}Recommandation :{'\n'}{maturity.recommendation}</Text>
            </View>
          </View>
        </View>
        <PageFooter page="2" total="7" contact={contactEmail} />
      </Page>

      {/* ── PAGE 3: DIMENSION ANALYSIS ───────────────────────────────────── */}
      <Page size="A4" style={s.contentPage}>
        <PageHeader title="Analyse Détaillée" client={clientName} />
        <View style={s.pageBody}>
          <Text style={s.chipLabel}>Analyse par dimension</Text>
          <Text style={s.sectionTitle}>Vos 4 piliers IAM décryptés</Text>
          <Text style={s.sectionSubtitle}>Chaque dimension révèle une facette de votre maturité actuelle et les leviers d'amélioration prioritaires.</Text>

          <View style={s.card}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <View style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: DIM_COLORS.gouvernance }} />
              <Text style={s.cardTitle}>Gouvernance IAM — {gouvernance}/25</Text>
            </View>
            <Text style={s.cardText}>
              {gouvernance >= 18 ? 'Excellente gouvernance : processus structurés, ownership clair. Auth0 viendra renforcer et automatiser vos pratiques existantes.' :
               gouvernance >= 12 ? 'Gouvernance en développement : des bases sont posées mais manquent de formalisation. Auth0 apportera un cadre robuste et des audits automatiques.' :
               'Gouvernance insuffisante : l\'absence de processus formalisés crée un risque opérationnel et réglementaire significatif. C\'est le premier chantier à adresser.'}
            </Text>
          </View>

          <View style={s.card}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <View style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: DIM_COLORS.securite }} />
              <Text style={s.cardTitle}>Sécurité — {securite}/25</Text>
            </View>
            <Text style={s.cardText}>
              {securite >= 18 ? 'Bonne posture de sécurité. Auth0 vous permettra d\'ajouter MFA adaptatif, détection d\'anomalies et protection contre les attaques en temps réel.' :
               securite >= 12 ? 'Sécurité partielle : certains mécanismes sont en place mais des vulnérabilités subsistent, notamment sur la gestion des credentials et des sessions.' :
               'Posture sécurité critique : vos utilisateurs sont exposés à des risques réels. Des pratiques non conformes aux standards OWASP représentent une menace directe.'}
            </Text>
          </View>

          <View style={s.card}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <View style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: DIM_COLORS.efficacite }} />
              <Text style={s.cardTitle}>Efficacité opérationnelle — {efficacite}/25</Text>
            </View>
            <Text style={s.cardText}>
              {efficacite >= 18 ? 'Vos équipes sont efficaces. Auth0 réduira encore davantage le temps dédié à l\'auth et libérera de la capacité pour votre cœur de métier.' :
               efficacite >= 10 ? 'Efficacité modérée : le temps consacré à l\'authentification reste significatif. Auth0 peut réduire ce coût de 60 à 80% selon nos benchmarks clients.' :
               'Coûts opérationnels élevés : vos équipes consacrent un temps disproportionné à l\'authentification. Chaque heure passée sur l\'auth est une heure non investie dans votre produit.'}
            </Text>
          </View>

          <View style={s.card}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <View style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: DIM_COLORS.strategie }} />
              <Text style={s.cardTitle}>Maturité stratégique — {strategie}/25</Text>
            </View>
            <Text style={s.cardText}>
              {strategie >= 18 ? 'Excellente vision stratégique : vous avez identifié les bons objectifs et un horizon réaliste. Votre projet Auth0 a toutes les chances de réussir rapidement.' :
               strategie >= 10 ? 'Vision en cours de structuration : vos objectifs sont identifiés mais le chemin n\'est pas encore complètement tracé. C\'est précisément ce que notre accompagnement adresse.' :
               'Stratégie IAM à définir : sans feuille de route claire, les risques de votre situation actuelle vont s\'aggraver. Une session avec notre expert est la première étape critique.'}
            </Text>
          </View>
        </View>
        <PageFooter page="3" total="7" contact={contactEmail} />
      </Page>

      {/* ── PAGE 4: RISK MATRIX ──────────────────────────────────────────── */}
      <Page size="A4" style={s.contentPage}>
        <PageHeader title="Matrice des Risques" client={clientName} />
        <View style={s.pageBody}>
          <Text style={s.chipLabel}>Risques identifiés</Text>
          <Text style={s.sectionTitle}>Ce que votre diagnostic révèle</Text>
          <Text style={s.sectionSubtitle}>Les risques suivants ont été identifiés sur la base de vos réponses. Ils ont un impact direct sur la sécurité, la conformité et la performance de votre activité.</Text>

          <View style={s.card}>
            {risks.length === 0 && (
              <Text style={s.cardText}>Aucun risque critique identifié. Votre maturité IAM est élevée — Auth0 optimisera encore votre infrastructure.</Text>
            )}
            {risks.map((r, i) => (
              <RiskItem
                key={i}
                color={r.level === 'critical' ? C.danger : r.level === 'high' ? C.warning : r.level === 'medium' ? C.accent : C.gray400}
                title={r.title}
                text={r.text}
              />
            ))}
          </View>

          {/* Légende */}
          <View style={{ flexDirection: 'row', gap: 16, marginTop: 4 }}>
            {[['Critique', C.danger], ['Élevé', C.warning], ['Modéré', C.accent], ['Faible', C.gray400]].map(([label, color]) => (
              <View key={label} style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: color }} />
                <Text style={{ fontSize: 8, color: C.gray600 }}>{label}</Text>
              </View>
            ))}
          </View>

          {/* Objectifs recap */}
          {objectifs.length > 0 && (
            <View style={[s.card, { marginTop: 20 }]}>
              <Text style={[s.cardTitle, { marginBottom: 10 }]}>Vos objectifs déclarés</Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                {objectifs.map(o => (
                  <View key={o} style={[s.pill, { borderColor: C.primary }]}>
                    <Text style={[s.pillText, { color: C.primary }]}>✓ {objectifLabels[o] || o}</Text>
                  </View>
                ))}
              </View>
              <Text style={[s.cardText, { marginTop: 8 }]}>
                Auth0 répond nativement à {objectifs.length > 1 ? 'tous ces objectifs' : 'cet objectif'} avec des fonctionnalités prêtes à l'emploi, sans développement custom.
              </Text>
            </View>
          )}
        </View>
        <PageFooter page="4" total="7" contact={contactEmail} />
      </Page>

      {/* ── PAGE 5: ROADMAP ──────────────────────────────────────────────── */}
      <Page size="A4" style={s.contentPage}>
        <PageHeader title="Plan de Migration Recommandé" client={clientName} />
        <View style={s.pageBody}>
          <Text style={s.chipLabel}>Notre approche</Text>
          <Text style={s.sectionTitle}>Votre feuille de route Auth0</Text>
          <Text style={s.sectionSubtitle}>Une migration Auth0 réussie suit 4 phases éprouvées. Notre méthodologie garantit zéro interruption de service et une adoption rapide par vos équipes.</Text>

          <View style={s.card}>
            {roadmap.map((phase, i) => (
              <PhaseItem
                key={i}
                num={i + 1}
                title={phase.title}
                text={phase.text}
                duration={`⏱ Durée estimée : ${phase.duration}`}
                color={phase.color}
              />
            ))}
          </View>

          {/* Why Auth0 */}
          <View style={[s.card, { borderColor: C.primary }]}>
            <Text style={[s.cardTitle, { marginBottom: 10 }]}>Pourquoi Auth0 est le bon choix</Text>
            <View style={{ gap: 6 }}>
              {[
                ['Zéro code', 'Authentification prête à l\'emploi en quelques heures, pas en mois'],
                ['Sécurité de niveau enterprise', 'SOC2, ISO27001, RGPD — certifié par défaut'],
                ['Scalabilité immédiate', 'De 100 à 100M d\'utilisateurs sans refonte'],
                ['Réduction coûts', '60-80% de réduction du temps développeur sur l\'auth'],
                ['Intégrations natives', '30+ providers sociaux, SAML, OIDC, LDAP/AD'],
              ].map(([title, text]) => (
                <View key={title} style={{ flexDirection: 'row', gap: 8, marginBottom: 4 }}>
                  <Text style={{ fontSize: 10, color: C.primary }}>▸</Text>
                  <Text style={{ fontSize: 9, color: C.gray400, flex: 1 }}><Text style={{ fontFamily: 'Helvetica-Bold', color: C.white }}>{title} — </Text>{text}</Text>
                </View>
              ))}
            </View>
          </View>
        </View>
        <PageFooter page="5" total="7" contact={contactEmail} />
      </Page>

      {/* ── PAGE 6: FREE TOOLS (LEAD MAGNET) ────────────────────────────── */}
      <Page size="A4" style={s.contentPage}>
        <PageHeader title="Outils Offerts" client={clientName} />
        <View style={s.pageBody}>
          <Text style={s.chipLabel}>Valeur ajoutée exclusive</Text>
          <Text style={s.sectionTitle}>Vos outils de migration gratuits</Text>
          <Text style={s.sectionSubtitle}>En tant que lead qualifié, vous avez accès à une boîte à outils exclusive développée par nos experts. Ces outils réduisent de 40% le temps de migration.</Text>

          <View style={s.card}>
            <ToolItem icon="🔍" name="Auth0 Migration Checker" desc="Script d'audit automatique de compatibilité entre votre stack actuelle et Auth0. Identifie en 5 minutes les points de friction et génère un rapport technique." locked />
            <ToolItem icon="🔐" name="Password Hash Converter" desc="Outil de conversion et validation des algorithmes de hashage (bcrypt, MD5, PBKDF2, Argon2). Compatible avec les fonctionnalités de migration progressive Auth0." locked />
            <ToolItem icon="📋" name="Auth0 Bulk Import Template" desc="Templates JSON et scripts prêts à l'emploi pour l'import en masse d'utilisateurs. Inclut la gestion des métadonnées et des rôles personnalisés." locked />
            <ToolItem icon="📘" name="Migration Runbook complet" desc="Plan d'exécution détaillé étape par étape : pré-migration, migration, validation, rollback. Inclut les checklists QA et les procédures de go-live." locked />
            <ToolItem icon="📊" name="ROI Calculator Auth0" desc="Calculateur personnalisé pour mesurer les économies réalisées avec Auth0 vs votre solution actuelle. Inclut coûts dev, incidents et conformité." locked />
          </View>

          <View style={[s.ctaBox, { marginTop: 8 }]}>
            <Text style={s.ctaTitle}>Débloquez vos 5 outils gratuits</Text>
            <Text style={s.ctaText}>
              Ces outils représentent plusieurs centaines d'heures de développement. Ils vous sont offerts en échange d'une session de 30 minutes avec l'un de nos experts en migration Auth0.
              {'\n\n'}Durant cette session, nous analyserons ensemble votre situation et vous repartirez avec un plan d'action concret et actionnable.
            </Text>
            <Text style={s.ctaHighlight}>→ Réservez votre session sur : auth0.expert/contact</Text>
          </View>
        </View>
        <PageFooter page="6" total="7" contact={contactEmail} />
      </Page>

      {/* ── PAGE 7: NEXT STEPS ───────────────────────────────────────────── */}
      <Page size="A4" style={s.contentPage}>
        <PageHeader title="Prochaines Étapes" client={clientName} />
        <View style={s.pageBody}>
          <Text style={s.chipLabel}>Passez à l'action</Text>
          <Text style={s.sectionTitle}>Votre plan d'action immédiat</Text>
          <Text style={s.sectionSubtitle}>3 étapes simples pour démarrer votre migration Auth0 et sécuriser votre infrastructure IAM.</Text>

          <View style={s.card}>
            <View style={s.stepRow}>
              <View style={s.stepCircle}><Text style={s.stepNum}>1</Text></View>
              <View style={s.stepContent}>
                <Text style={s.stepTitle}>Réservez votre session expert gratuite (30 min)</Text>
                <Text style={s.stepText}>Un expert Auth0 certifié analyse votre situation spécifique et vous propose un plan de migration personnalisé. Vous repartez avec un devis indicatif et vos 5 outils gratuits débloqués.</Text>
                <Text style={{ fontSize: 9, color: C.primary, marginTop: 3 }}>→ auth0.expert/contact</Text>
              </View>
            </View>
            <View style={s.stepRow}>
              <View style={[s.stepCircle, { backgroundColor: C.secondary }]}><Text style={s.stepNum}>2</Text></View>
              <View style={s.stepContent}>
                <Text style={s.stepTitle}>Recevez votre proposition technique sous 48h</Text>
                <Text style={s.stepText}>Suite à la session, notre équipe prépare une proposition technique détaillée avec architecture cible, planning de migration et estimation budgétaire précise.</Text>
              </View>
            </View>
            <View style={[s.stepRow, { marginBottom: 0 }]}>
              <View style={[s.stepCircle, { backgroundColor: C.success }]}><Text style={s.stepNum}>3</Text></View>
              <View style={s.stepContent}>
                <Text style={s.stepTitle}>Lancez votre migration en toute sérénité</Text>
                <Text style={s.stepText}>Notre équipe prend en charge l'intégralité de la migration avec garantie de zéro interruption de service. Vous conservez le contrôle à chaque étape.</Text>
              </View>
            </View>
          </View>

          {/* Guarantees */}
          <View style={[s.cardRow, { marginBottom: 8 }]}>
            <View style={s.cardHalf}>
              <Text style={s.cardTitle}>✅ Nos garanties</Text>
              <Text style={s.cardText}>• Zéro interruption de service{'\n'}• Données sécurisées et chiffrées{'\n'}• Rollback garanti si nécessaire{'\n'}• Support dédié 3 mois post-migration</Text>
            </View>
            <View style={s.cardHalf}>
              <Text style={s.cardTitle}>📞 Contact direct</Text>
              <Text style={s.cardText}>{contactEmail}{'\n'}+33 (0)1 XX XX XX XX{'\n\n'}Réponse sous 2h ouvrées</Text>
            </View>
          </View>

          <View style={[s.card, { backgroundColor: '#0D1A0D', borderColor: C.success }]}>
            <Text style={{ fontSize: 10, color: C.success, fontFamily: 'Helvetica-Bold', marginBottom: 4 }}>
              🎯 Offre limitée — Diagnostic approfondi offert
            </Text>
            <Text style={{ fontSize: 9, color: C.gray400, lineHeight: 1.5 }}>
              Pour les {objectifs.length > 0 ? objectifs.length : 'quelques'} prochains leads qualifiés, nous offrons un audit technique complet de votre stack d'authentification (valeur 1 500€). Profitez-en en réservant votre session cette semaine.
            </Text>
          </View>
        </View>
        <PageFooter page="7" total="7" contact={contactEmail} />
      </Page>

    </Document>
  );
}

// helper used in JSX (not exportable from config to avoid import cycle)
function wizardConfig_totalQuestions(answers) {
  let count = 0;
  if (answers.perimetre) count += Object.keys(answers.perimetre).length;
  if (answers.gestion) count += Object.keys(answers.gestion).length;
  if (answers.risques) count += Object.keys(answers.risques).length;
  if (answers.ambitions) count += Object.keys(answers.ambitions).length;
  return count || 11;
}
